import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { billOperatorsAPI, planApiCirclesAPI, planApiOperatorsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Plus, Trash2, Save, Search, Edit2, X, Check,
  Globe, Zap, Car, Banknote, Flame, Tv, Smartphone,
  MapPin, ChevronRight, Home, RefreshCw, Filter,
} from 'lucide-react';

const API_TYPE_LABELS = {
  recharge_plan: 'Recharge Plan',
  dth_plan: 'DTH Plan',
  dth_info: 'DTH Customer Info',
  dth_heavy: 'DTH Heavy Refresh',
};

const SERVICE_ICONS = {
  electricity: Zap, fastag: Car, loan: Banknote, piped_gas: Flame,
  cylinder: Flame, broadband: Globe, dth: Tv, prepaid: Smartphone,
};

// ===== INLINE EDIT ROW =====
const EditableRow = ({ item, fields, onSave, onCancel }) => {
  const [data, setData] = useState({ ...item });
  return (
    <tr className="bg-blue-50/40">
      {fields.map(f => (
        <td key={f.key} className="px-3 py-2">
          {f.type === 'select' ? (
            <select className="w-full h-8 text-xs border rounded-lg px-2 bg-white" value={data[f.key] || ''} onChange={e => setData({ ...data, [f.key]: e.target.value })}>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <Input className="h-8 text-xs" value={data[f.key] || ''} onChange={e => setData({ ...data, [f.key]: e.target.value })} placeholder={f.placeholder || ''} />
          )}
        </td>
      ))}
      <td className="px-3 py-2">
        <div className="flex gap-1">
          <Button size="sm" className="h-7 w-7 p-0 bg-emerald-600 hover:bg-emerald-700" onClick={() => onSave(data)}><Check className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onCancel}><X className="w-3 h-3" /></Button>
        </div>
      </td>
    </tr>
  );
};

// ===== BILL OPERATORS SECTION =====
const BillOperatorsSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);

  const SERVICE_TYPES = [
    { value: 'electricity', label: 'Electricity' }, { value: 'fastag', label: 'FASTag' },
    { value: 'loan', label: 'Loan' }, { value: 'piped_gas', label: 'Piped Gas' },
    { value: 'cylinder', label: 'Book a Cylinder' }, { value: 'broadband', label: 'Broadband' },
  ];

  const fetchData = async () => {
    try {
      const res = await billOperatorsAPI.list();
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = items.filter(i => {
    const matchSearch = !search || i.operator_name?.toLowerCase().includes(search.toLowerCase()) || i.operator_code?.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || i.service_type === filterType;
    return matchSearch && matchType;
  });

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (data.id) {
        await billOperatorsAPI.update(data.id, data);
      } else {
        await billOperatorsAPI.create(data);
      }
      setEditId(null); setAdding(false); fetchData();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this operator?')) return;
    try { await billOperatorsAPI.delete(id); fetchData(); } catch (e) { console.error(e); }
  };

  const handleToggle = async (item) => {
    const newStatus = item.status === 'active' ? 'inactive' : 'active';
    try { await billOperatorsAPI.update(item.id, { ...item, status: newStatus }); fetchData(); } catch (e) { console.error(e); }
  };

  const fields = [
    { key: 'service_type', type: 'select', options: SERVICE_TYPES.map(s => ({ value: s.value, label: s.label })) },
    { key: 'operator_name', placeholder: 'Operator Name' },
    { key: 'operator_code', placeholder: 'API Code (e.g. WBSE)' },
    { key: 'field1_label', placeholder: 'Field1 Label (optional)' },
  ];

  return (
    <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="bill-operators-section">
      <CardHeader className="pb-3 pt-5 px-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base font-bold text-slate-800">Bill Fetch Operators ({filtered.length})</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input className="h-8 text-xs pl-8 w-40" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="h-8 text-xs border rounded-lg px-2 bg-white" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              {SERVICE_TYPES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <Button size="sm" className="h-8 text-xs bg-[#4680ff]" onClick={() => setAdding(true)} data-testid="add-bill-op-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {loading ? <Skeleton className="h-40 w-full" /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">Service</th>
                <th className="px-3 py-2 text-left font-semibold">Operator</th>
                <th className="px-3 py-2 text-left font-semibold">Code</th>
                <th className="px-3 py-2 text-left font-semibold">Field1</th>
                <th className="px-3 py-2 text-left font-semibold w-28">Actions</th>
              </tr></thead>
              <tbody>
                {adding && <EditableRow item={{ service_type: 'electricity', operator_name: '', operator_code: '', field1_label: '' }} fields={fields} onSave={handleSave} onCancel={() => setAdding(false)} />}
                {filtered.map(item => (
                  editId === item.id ? (
                    <EditableRow key={item.id} item={item} fields={fields} onSave={handleSave} onCancel={() => setEditId(null)} />
                  ) : (
                    <tr key={item.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{item.service_type}</Badge></td>
                      <td className="px-3 py-2 font-medium text-slate-700">{item.operator_name}</td>
                      <td className="px-3 py-2 font-mono text-[#4680ff]">{item.operator_code}</td>
                      <td className="px-3 py-2 text-slate-400">{item.field1_label || '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(item.id)}><Edit2 className="w-3 h-3 text-slate-500" /></Button>
                          <Button size="sm" variant="ghost" className={`h-6 px-1.5 text-[10px] ${item.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`} onClick={() => handleToggle(item)}>
                            {item.status === 'active' ? 'ON' : 'OFF'}
                          </Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDelete(item.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== CIRCLES SECTION =====
const CirclesSection = () => {
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);

  const fetchData = async () => {
    try { const res = await planApiCirclesAPI.list(); setCircles(res.data.circles || res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async (data) => {
    try {
      if (data.id) { await planApiCirclesAPI.update(data.id, data); }
      else { await planApiCirclesAPI.create(data); }
      setEditId(null); setAdding(false); fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this circle?')) return;
    try { await planApiCirclesAPI.delete(id); fetchData(); } catch (e) { console.error(e); }
  };

  const fields = [
    { key: 'circle_code', placeholder: 'Code (e.g. 51)' },
    { key: 'circle_name', placeholder: 'Circle Name (e.g. West Bengal)' },
    { key: 'status', type: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
  ];

  return (
    <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="circles-section">
      <CardHeader className="pb-3 pt-5 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#4680ff]" />
            <CardTitle className="text-base font-bold text-slate-800">Circles ({circles.length})</CardTitle>
          </div>
          <Button size="sm" className="h-8 text-xs bg-[#4680ff]" onClick={() => setAdding(true)} data-testid="add-circle-btn">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Circle
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {loading ? <Skeleton className="h-32 w-full" /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">Code</th>
                <th className="px-3 py-2 text-left font-semibold">Circle Name</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold w-24">Actions</th>
              </tr></thead>
              <tbody>
                {adding && <EditableRow item={{ circle_code: '', circle_name: '', status: 'active' }} fields={fields} onSave={handleSave} onCancel={() => setAdding(false)} />}
                {circles.map(c => (
                  editId === c.id ? (
                    <EditableRow key={c.id} item={c} fields={fields} onSave={handleSave} onCancel={() => setEditId(null)} />
                  ) : (
                    <tr key={c.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2 font-mono text-[#4680ff] font-bold">{c.circle_code}</td>
                      <td className="px-3 py-2 font-medium text-slate-700">{c.circle_name}</td>
                      <td className="px-3 py-2">
                        <Badge variant={c.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">{c.status}</Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(c.id)}><Edit2 className="w-3 h-3 text-slate-500" /></Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDelete(c.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== PLAN API OPERATORS SECTION =====
const PlanOperatorsSection = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [filterApi, setFilterApi] = useState('');

  const fetchData = async () => {
    try { const res = await planApiOperatorsAPI.list(); setOperators(res.data.operators || res.data || []); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = operators.filter(o => !filterApi || o.api_type === filterApi);

  const handleSave = async (data) => {
    try {
      if (data.id) { await planApiOperatorsAPI.update(data.id, data); }
      else { await planApiOperatorsAPI.create(data); }
      setEditId(null); setAdding(false); fetchData();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this operator?')) return;
    try { await planApiOperatorsAPI.delete(id); fetchData(); } catch (e) { console.error(e); }
  };

  const fields = [
    { key: 'api_type', type: 'select', options: Object.entries(API_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })) },
    { key: 'service_type', type: 'select', options: [{ value: 'prepaid', label: 'Prepaid' }, { value: 'dth', label: 'DTH' }] },
    { key: 'op_name', placeholder: 'OPName (e.g. Jio, Tata Sky)' },
    { key: 'status', type: 'select', options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }] },
  ];

  return (
    <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="plan-operators-section">
      <CardHeader className="pb-3 pt-5 px-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base font-bold text-slate-800">Plan API Operators ({filtered.length})</CardTitle>
          <div className="flex items-center gap-2">
            <select className="h-8 text-xs border rounded-lg px-2 bg-white" value={filterApi} onChange={e => setFilterApi(e.target.value)}>
              <option value="">All API Types</option>
              {Object.entries(API_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Button size="sm" className="h-8 text-xs bg-[#4680ff]" onClick={() => setAdding(true)} data-testid="add-plan-op-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Add
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-3">
        {loading ? <Skeleton className="h-32 w-full" /> : (
          <div className="overflow-x-auto rounded-lg border border-slate-100">
            <table className="w-full text-xs">
              <thead><tr className="bg-slate-50 text-slate-500">
                <th className="px-3 py-2 text-left font-semibold">API Type</th>
                <th className="px-3 py-2 text-left font-semibold">Service</th>
                <th className="px-3 py-2 text-left font-semibold">OPName</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold w-24">Actions</th>
              </tr></thead>
              <tbody>
                {adding && <EditableRow item={{ api_type: 'recharge_plan', service_type: 'prepaid', op_name: '', status: 'active' }} fields={fields} onSave={handleSave} onCancel={() => setAdding(false)} />}
                {filtered.map(op => (
                  editId === op.id ? (
                    <EditableRow key={op.id} item={op} fields={fields} onSave={handleSave} onCancel={() => setEditId(null)} />
                  ) : (
                    <tr key={op.id} className="border-t border-slate-50 hover:bg-slate-50/50">
                      <td className="px-3 py-2"><Badge className="text-[10px] bg-blue-50 text-[#4680ff] border-blue-200" variant="outline">{API_TYPE_LABELS[op.api_type] || op.api_type}</Badge></td>
                      <td className="px-3 py-2 capitalize text-slate-600">{op.service_type}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800">{op.op_name}</td>
                      <td className="px-3 py-2"><Badge variant={op.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">{op.status}</Badge></td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(op.id)}><Edit2 className="w-3 h-3 text-slate-500" /></Button>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => handleDelete(op.id)}><Trash2 className="w-3 h-3 text-red-400" /></Button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ===== MAIN PAGE =====
export default function PlanApiManager() {
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="plan-api-manager">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Home className="w-4 h-4" /><ChevronRight className="w-3 h-3" />
          <span className="text-slate-400">Admin</span><ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">Plan API Manager</span>
        </div>

        <div>
          <h1 className="text-2xl font-bold text-slate-900">Plan API Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Manage Bill Fetch operators, Recharge Plan operators, Circles & DTH configs</p>
        </div>

        <BillOperatorsSection />
        <CirclesSection />
        <PlanOperatorsSection />
      </div>
    </DashboardLayout>
  );
}
